import { TagSchemaField } from '@/core/notifications/model';
import {
  fieldLeafKeys,
  fieldSentenceLabel,
  isFlatAllowedList,
} from '@/core/notifications/model/tag-schema';
import { ScopeMode } from '@/core/notifications/components/subscription-wizard/use-subscription-wizard-state';
import TagAllowedTree from '@/core/notifications/components/settings/subscriptions/tag-allowed-tree';
import FlatAllowedList from '@/core/notifications/components/settings/subscriptions/flat-allowed-list';

// One tag field per screen: "Receive all {field}" vs "Narrow {field}", with
// the value picker shown inline (no collapsible chrome needed — the whole
// screen is already dedicated to this one field) once narrowed.
export default function StepScopeField({
  tagKey,
  field,
  mode,
  selectedValues,
  onModeChange,
  onToggleLeaf,
  onToggleGroup,
  onFreeText,
}: {
  tagKey: string;
  field: TagSchemaField;
  mode: ScopeMode;
  selectedValues: string[];
  onModeChange: (mode: ScopeMode) => void;
  onToggleLeaf: (leafKey: string) => void;
  onToggleGroup: (leafKeys: string[], checked: boolean) => void;
  onFreeText: (raw: string) => void;
}) {
  const fieldLabel = fieldSentenceLabel(field, tagKey);
  const hasAllowedList = !!field.allowed && field.allowed.length > 0;
  const leaves = hasAllowedList ? fieldLeafKeys(field) : [];
  // stored values that are no longer leaves (removed keys, or keys that became groups)
  const invalidValues = hasAllowedList
    ? selectedValues.filter(value => !leaves.includes(value))
    : [];

  return (
    <div>
      {field.description && (
        <div className="mutedText mb-3" style={{ fontSize: 13 }}>
          {field.description}
        </div>
      )}

      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            id={`scope-field-${tagKey}-all`}
            checked={mode === 'all'}
            disabled={field.required}
            onChange={() => onModeChange('all')}
          />
          <label className="form-check-label" htmlFor={`scope-field-${tagKey}-all`}>
            Receive every {fieldLabel}
          </label>
          {field.required && (
            <span className="text-danger ms-2" style={{ fontSize: 12 }}>
              required — can't receive every
            </span>
          )}
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="radio"
            id={`scope-field-${tagKey}-narrow`}
            checked={mode === 'narrow'}
            onChange={() => onModeChange('narrow')}
          />
          <label className="form-check-label" htmlFor={`scope-field-${tagKey}-narrow`}>
            Narrow {fieldLabel}
          </label>
        </div>
      </div>

      {invalidValues.length > 0 && (
        <div className="mb-2">
          {invalidValues.map(value => (
            <span
              key={value}
              className="badge me-1 fw-semibold"
              style={{
                background: 'rgba(var(--bs-danger-rgb), 0.2)',
                color: 'var(--bs-danger)',
                borderRadius: 2,
              }}
            >
              {value}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1"
                style={{ color: 'var(--bs-danger)' }}
                onClick={() => onToggleLeaf(value)}
                aria-label={`Remove ${value}`}
              >
                ×
              </button>
            </span>
          ))}
          <div style={{ fontSize: 12, color: 'var(--bs-warning)' }}>
            These values are no longer available for this field. Remove them to continue.
          </div>
        </div>
      )}

      {mode === 'narrow' &&
        (hasAllowedList ? (
          isFlatAllowedList(field) ? (
            <FlatAllowedList
              entries={field.allowed ?? []}
              selected={selectedValues}
              onToggleLeaf={onToggleLeaf}
            />
          ) : (
            <TagAllowedTree
              entries={field.allowed ?? []}
              selected={selectedValues}
              onToggleLeaf={onToggleLeaf}
              onToggleGroup={onToggleGroup}
            />
          )
        ) : (
          <input
            className="form-control"
            placeholder="Comma-separate multiple values"
            value={selectedValues.join(', ')}
            onChange={event => onFreeText(event.target.value)}
          />
        ))}
    </div>
  );
}
